import { useMemo } from 'react'
import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { buildFilterKey } from '@/services/filterEngine'
import { getResponseColor } from '@/utils/colors'

// Response order: A lot → Some → Unsure → Little → No confidence
const CONFIDENCE_RESPONSE_ORDER = ['A lot of confidence', 'Some confidence', 'Unsure', 'Little confidence', 'No confidence']

// Sort responses according to confidence order (Unsure in the middle)
const sortConfidenceResponses = (responses: Array<{ label: string; percentage: number }>) => {
  return [...responses].sort((a, b) => {
    const aIdx = CONFIDENCE_RESPONSE_ORDER.indexOf(a.label)
    const bIdx = CONFIDENCE_RESPONSE_ORDER.indexOf(b.label)
    if (aIdx === -1) return 1
    if (bIdx === -1) return -1
    return aIdx - bIdx
  })
}

const CONFIDENCE_QUESTIONS = [
  { id: 'Conf_LocalScientists', label: 'Local scientists and wildlife experts' },
  { id: 'Conf_ScientistsExperts', label: 'Scientists and wildlife experts' },
  { id: 'Conf_FarmersRanchers', label: 'Farmers and ranchers' },
  { id: 'Conf_ParkRangers', label: 'Provincial park rangers and conservation officers' },
  { id: 'Conf_ParksCanada', label: 'Parks Canada' },
  { id: 'Conf_LocalCommunities', label: 'Local communities' },
  { id: 'Conf_NatConservGroups', label: 'National conservation groups like Canadian Parks and Wilderness Society (CPAWS)' },
  { id: 'Conf_SciShows', label: 'Science shows like National Geographic, Discovery Channel, nature documentaries' },
  { id: 'Conf_LocalResidents', label: 'Local residents' },
  { id: 'Conf_IndigenousElders', label: 'Local Indigenous elders' },
  { id: 'Conf_LocalIndigenous', label: 'Local Indigenous communities' },
  { id: 'Conf_IndigenousGuardians', label: 'Local Indigenous guardians' },
  { id: 'Conf_EnvClimateCanada', label: 'Environment & Climate Change Canada' },
  { id: 'Conf_LocalIndigenousGovt', label: 'Local Indigenous governments' },
  { id: 'Conf_EnvGroups', label: 'Environmental groups' },
  { id: 'Conf_LocalHunters', label: 'Local hunters and anglers' },
  { id: 'Conf_NationalMedia', label: 'National news media (e.g. CTV, Global)' },
  { id: 'Conf_UN', label: 'The United Nations' },
  { id: 'Conf_LocalBusinesses', label: 'Local businesses' },
  { id: 'Conf_ForestryCompanies', label: 'Forestry companies' },
  { id: 'Conf_ResourceIndustries', label: 'Resource industries' },
  { id: 'Conf_MiningCompanies', label: 'Mining companies' },
  { id: 'Conf_OilGasCompanies', label: 'Oil and gas companies' },
  { id: 'Conf_RealEstateDevelopers', label: 'Real estate developers' },
] as const

export function ConfidenceInSources() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  // Sort questions by confidence level (A lot + Some confidence) - highest first
  const sortedQuestions = useMemo(() => {
    if (!data) return CONFIDENCE_QUESTIONS

    const totalFilterKey = buildFilterKey('total', 'total')

    return [...CONFIDENCE_QUESTIONS].sort((a, b) => {
      const aData = data.questions[a.id]?.data[totalFilterKey]
      const bData = data.questions[b.id]?.data[totalFilterKey]

      const getConfidenceScore = (qData: typeof aData) => {
        if (!qData) return 0
        const aLot = qData.responses.find(r => r.label === 'A lot of confidence')?.percentage || 0
        const some = qData.responses.find(r => r.label === 'Some confidence')?.percentage || 0
        return aLot + some
      }

      return getConfidenceScore(bData) - getConfidenceScore(aData)
    })
  }, [data])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Confidence in Information Sources</h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">Break: {breakName}</span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          How much confidence do you have in each of the following sources of information about environmental and climate issues?
        </p>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-slate-100 dark:border-slate-700">
          {CONFIDENCE_RESPONSE_ORDER.map(label => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getResponseColor('confidence', label) }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading...</p>
          ) : (
            sortedQuestions.map(({ id, label }) => {
              const question = data.questions[id]
              if (!question) return null

              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined, selectedProvince)
                let questionData = question.data[filterKey]
                if (!questionData && selectedProvince) {
                  questionData = question.data[buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)]
                }
                return { value, filterKey, data: questionData }
              }).filter(row => row.data)

              if (rows.length === 0) return null

              return (
                <div key={id} className="border-b border-slate-100 dark:border-slate-700 pb-3 last:border-0 last:pb-0">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{label}</h3>
                  <div className="space-y-1">
                    {rows.map(row => (
                      <div key={row.filterKey} className="flex items-center gap-2">
                        <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0" title={isBreakActive ? getDisplayName(row.value) : 'National Sample'}>
                          {isBreakActive ? getDisplayName(row.value) : 'National Sample'}
                        </div>
                        <div className="flex-1">
                          <HorizontalStackedBar
                            segments={sortConfidenceResponses(row.data!.responses)}
                            scaleType="confidence"
                            height={isBreakActive ? 20 : 24}
                            showLabels={false}
                            normalize={true}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
